import React, { useState, useRef, useEffect } from "react";
import type Konva from "konva";
import {
    Stage,
    Layer,
    Rect,
    Line,
    Text,
    Group,
    Image as CanvasImage,
} from "react-konva";
import { Theme, Themes } from "../themes";
import type { Metadata } from "../minidisc-labeler";
import { changeDpiDataUrl } from "changedpi";
import WebFont from "webfontloader";

const PPM_FACTOR = 300 / 25.4;
const MDLogoImage = new URL("../../images/md-logo.png", import.meta.url).href;

function useLoadedImage(src: string) {
    const [loaded, setLoaded] = useState<{ src: string; image: HTMLImageElement } | null>(null);
    const [failed, setFailed] = useState("");
    useEffect(() => {
        let cancelled = false;
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => { if (!cancelled) setLoaded({ src, image }); };
        image.onerror = () => { if (!cancelled) setFailed(src); };
        image.src = src;
        return () => { cancelled = true; };
    }, [src]);
    return { image: loaded && loaded.src === src ? loaded.image : undefined, failed: failed === src };
}

function calculateCentering(
    containerHeight: number,
    elementHeight: number,
    yOffset = 0
): number {
    return (containerHeight - elementHeight) / 2 + yOffset;
}

const Meta: React.FC<{
    artist: string;
    album: string;
    year: string;
    color: string;
    fontSize: number;
    lineHeight: number;
    containerHeight: number;
    yOffset: number;
    margin: number;
}> = ({
    artist,
    album,
    year,
    color,
    fontSize,
    lineHeight,
    containerHeight,
    yOffset,
    margin,
}) => {
    const metaContentHeight = lineHeight + lineHeight + fontSize;
    const y = calculateCentering(containerHeight, metaContentHeight, yOffset);
    return (
        <Group y={y}>
            <Text
                fontSize={fontSize}
                text={album}
                fontFamily="futura-pt-bold, Arial, sans-serif"
                fontVariant="bold"
                fill={color}
                x={margin}
                y={0}
                lineHeight={fontSize}
            />
            <Text
                fontSize={fontSize}
                text={artist}
                fontFamily="futura-pt-bold, Arial, sans-serif"
                fontVariant="bold"
                fill={color}
                x={margin}
                y={lineHeight}
                lineHeight={fontSize}
            />
            <Text
                fontSize={fontSize}
                text={year}
                fontFamily="futura-pt-bold, Arial, sans-serif"
                fontVariant="bold"
                fill={color}
                x={margin}
                y={lineHeight * 2}
                lineHeight={fontSize}
            />
        </Group>
    );
};

export type LabelerProps = {
    width: number;
    height: number;
    theme: Theme;
    leftMargin: number;
    headerHeight: number;
    lineHeight: number;
    fontSize: number;
    metaContainerHeight: number;
    meta: Metadata;
    uppercase: boolean;
    onAddToSheet?: (label: { dataUrl: string; album: string }) => void;
};

export const Labeler: React.FC<LabelerProps> = ({
    width,
    height,
    theme = Themes.Dark,
    leftMargin,
    headerHeight,
    fontSize,
    lineHeight,
    metaContainerHeight,
    meta,
    uppercase,
    onAddToSheet,
}) => {
    const ARROW_HEIGHT = 1.25;
    const ARROW_Y = calculateCentering(headerHeight, ARROW_HEIGHT, 0);
    const stage = useRef<Konva.Stage>(null);
    const [fontLoaded, setFontLoaded] = useState(false);
    const [fontSettled, setFontSettled] = useState(false);
    const [error, setError] = useState("");
    const artwork = useLoadedImage(meta.artURL);
    const logo = useLoadedImage(MDLogoImage);
    const ready = !!artwork.image && !!logo.image && fontSettled;
    const artScale = artwork.image ? width / Math.max(artwork.image.width, artwork.image.height) : 0;
    const artWidth = artwork.image ? artwork.image.width * artScale : 0;
    const artHeight = artwork.image ? artwork.image.height * artScale : 0;
    const logoWidth = logo.image ? logo.image.width / PPM_FACTOR : 0;
    const logoHeight = logo.image ? logo.image.height / PPM_FACTOR : 0;

    const snapshot = () => {
        if (!stage.current || !ready) throw new Error("Label is still loading.");
        stage.current.draw();
        return changeDpiDataUrl(stage.current.toDataURL(), 300);
    };

    const downloadArt: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
        try {
            window.sa_event("download_art");
            e.currentTarget.download = `${meta.album}-label.png`;
            e.currentTarget.href = snapshot();
            setError("");
        } catch {
            e.preventDefault();
            setError("Unable to export this label. Wait for it to load or try another artwork image.");
        }
    };

    const redraw = () => {
        if (!stage.current) return;

        window.sa_event("redraw_art");

        stage.current.draw();
    };

    useEffect(() => {
        let cancelled = false;
        // A blocked external font must never leave exports disabled indefinitely.
        const timer = window.setTimeout(() => { if (!cancelled) setFontSettled(true); }, 3500);
        WebFont.load({
            typekit: {
                id: "elb5ydo",
            },
            active: () => { if (!cancelled) { setFontLoaded(true); setFontSettled(true); } },
            inactive: () => { if (!cancelled) setFontSettled(true); },
        });
        return () => { cancelled = true; window.clearTimeout(timer); };
    }, []);

    return (
        <>
            <Stage
                ref={stage}
                width={width * PPM_FACTOR}
                height={height * PPM_FACTOR}
                scaleX={PPM_FACTOR}
                scaleY={PPM_FACTOR}
            >
                <Layer>
                    <Rect height={height} width={width} fill={theme.bgColor} />
                </Layer>
                <Layer>
                    <Line
                        fill={theme.fgColor}
                        closed={true}
                        points={[
                            leftMargin + ARROW_HEIGHT,
                            ARROW_Y,
                            leftMargin + ARROW_HEIGHT * 2,
                            ARROW_Y + ARROW_HEIGHT,
                            leftMargin,
                            ARROW_Y + ARROW_HEIGHT,
                        ]}
                    />
                    {(
                        <Text
                            offsetY={fontSize}
                            x={5.5}
                            y={ARROW_HEIGHT + ARROW_Y}
                            fontSize={fontSize}
                            fontFamily={fontLoaded ? "futura-pt-bold" : "Arial, sans-serif"}
                            fontVariant="bold"
                            text="INSERT THIS END"
                            fill={theme.fgColor}
                            lineHeight={fontSize}
                        />
                    )}
                    <CanvasImage
                        image={logo.image}
                        y={calculateCentering(headerHeight, logoHeight)}
                        x={width - leftMargin - logoWidth}
                        width={logoWidth}
                        height={logoHeight}
                    />
                    <CanvasImage
                        image={artwork.image}
                        height={artHeight}
                        width={artWidth}
                        x={calculateCentering(width, artWidth)}
                        y={calculateCentering(width, artHeight, headerHeight)}
                    />
                    {(
                        <Meta
                            album={
                                uppercase
                                    ? meta.album.toUpperCase()
                                    : meta.album
                            }
                            artist={
                                uppercase
                                    ? meta.artist.toUpperCase()
                                    : meta.artist
                            }
                            year={
                                uppercase ? meta.year.toUpperCase() : meta.year
                            }
                            color={theme.fgColor}
                            containerHeight={metaContainerHeight}
                            fontSize={fontSize}
                            lineHeight={lineHeight}
                            yOffset={width + headerHeight}
                            margin={leftMargin}
                        />
                    )}
                </Layer>
            </Stage>
            <p>
                <a className={`btn${!ready ? " disabled" : ""}`} href="#" aria-disabled={!ready} onClick={downloadArt}>
                    download label
                </a>
                {onAddToSheet && <button type="button" className="btn" disabled={!ready} onClick={() => {
                    try {
                        onAddToSheet({ dataUrl: snapshot(), album: meta.album });
                        setError("");
                    } catch {
                        setError("Unable to add this label. Try another artwork image.");
                    }
                }}>Add to A4 sheet</button>}
                <button type="button" className="btn" onClick={redraw}>
                    Redraw preview
                </button>
            </p>
            {!ready && !artwork.failed && !logo.failed && <p role="status">Preparing label…</p>}
            {(artwork.failed || logo.failed) && <p role="alert">The label image could not be loaded. Please choose another artwork image or reload the page.</p>}
            {error && <p role="alert">{error}</p>}
        </>
    );
};
